import { Component } from 'react';
import cx from 'classnames';
import { ChartMetaFormField, ContentItem } from 'models/ui';
import { REFERRAL_CHILD_PREFIX, REFERRAL_PARENT_VALUE } from './constants';

interface NestedMultiselectDropdownProps {
  chartFormField: ChartMetaFormField;
  isDisabled?: boolean;
  ariaRequired?: boolean;
}

interface NestedMultiselectDropdownState {
  isOpen: boolean;
}

class NestedMultiselectDropdown extends Component<NestedMultiselectDropdownProps, NestedMultiselectDropdownState> {
  static displayName = 'NestedMultiselectDropdown';
  private wrapperRef: HTMLDivElement | null = null;

  constructor(props: NestedMultiselectDropdownProps) {
    super(props);
    this.state = { isOpen: false };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.wrapperRef && !this.wrapperRef.contains(event.target as Node)) {
      this.setState({ isOpen: false });
    }
  };

  getVisibleOptions = (chartFormField: ChartMetaFormField = this.props.chartFormField): ContentItem[] => {
    return chartFormField.chartContent?.filter((item) => !item.isHidden && (item.isActive || item.isActive === undefined)) ?? [];
  };

  isChildOfReferral = (option: ContentItem): boolean => {
    return option.value !== REFERRAL_PARENT_VALUE && option.label?.startsWith(REFERRAL_CHILD_PREFIX);
  };

  isReferralParent = (option: ContentItem): boolean => {
    return option.value === REFERRAL_PARENT_VALUE;
  };

  getParentOptions = (chartFormField: ChartMetaFormField = this.props.chartFormField): ContentItem[] => {
    return this.getVisibleOptions(chartFormField).filter((item) => !this.isChildOfReferral(item));
  };

  getChildOptions = (chartFormField: ChartMetaFormField = this.props.chartFormField): ContentItem[] => {
    return this.getVisibleOptions(chartFormField).filter((item) => this.isChildOfReferral(item));
  };

  isParentWithChildren = (option: ContentItem): boolean => {
    return this.isReferralParent(option) && this.getChildOptions().length > 0;
  };

  getParentState = (parentOption: ContentItem, chartFormField: ChartMetaFormField = this.props.chartFormField): 'none' | 'all' | 'indeterminate' => {
    if (!this.isReferralParent(parentOption)) return 'none';
    const children = this.getChildOptions(chartFormField);
    if (children.length === 0) return 'none';
    const selectedCount = children.filter((c) => c.selected).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === children.length) return 'all';
    return 'indeterminate';
  };
  batchUpdateSelections = (desiredSelections: Map<string, boolean>): void => {
    const { chartFormField } = this.props;
    if (desiredSelections.size === 0) return;

    const newChartContent = chartFormField.chartContent?.map((item) =>
      desiredSelections.has(item.id) ? { ...item, selected: desiredSelections.get(item.id) as boolean } : item
    );

    const newValue =
      (newChartContent ?? chartFormField.chartContent ?? [])
        .filter((item) => item.selected && !item.isHidden && (item.isActive || item.isActive === undefined))
        .map((item) => item.value)
        .join('|') || '';

    const desiredField: ChartMetaFormField = { ...chartFormField, chartContent: newChartContent, value: newValue };
    chartFormField.onChange?.(desiredField, { target: { value: newValue } });
  };

  handleParentClick = (option: ContentItem) => {
    const { chartFormField } = this.props;
    const currentOption = chartFormField.chartContent?.find((item) => item.id === option.id);
    if (!currentOption) return;

    const children = this.isReferralParent(currentOption) ? this.getChildOptions() : [];
    if (children.length === 0) {
      currentOption.onChange?.(chartFormField, currentOption);
      return;
    }

    const parentState = this.getParentState(currentOption);
    const desiredSelections = new Map<string, boolean>();

    if (parentState === 'all') {
      desiredSelections.set(currentOption.id, false);
      children.forEach((child) => desiredSelections.set(child.id, false));
    } else {
      desiredSelections.set(currentOption.id, true);
      children.forEach((child) => desiredSelections.set(child.id, true));
    }

    this.batchUpdateSelections(desiredSelections);
  };

  handleChildClick = (childOption: ContentItem, parentOption: ContentItem) => {
    const { chartFormField } = this.props;
    const currentChild = chartFormField.chartContent?.find((item) => item.id === childOption.id);
    const currentParent = chartFormField.chartContent?.find((item) => item.id === parentOption.id);
    if (!currentChild || !currentParent) return;

    const childWillBeSelected = !currentChild.selected;
    const desiredSelections = new Map<string, boolean>();

    desiredSelections.set(currentChild.id, childWillBeSelected);

    const otherChildrenSelected = this.getChildOptions().some((child) => child.id !== currentChild.id && child.selected);
    desiredSelections.set(currentParent.id, childWillBeSelected || otherChildrenSelected);

    this.batchUpdateSelections(desiredSelections);
  };

  clearAllSelections = () => {
    const desiredSelections = new Map<string, boolean>();
    (this.props.chartFormField.chartContent ?? [])
      .filter((item) => item.selected)
      .forEach((item) => desiredSelections.set(item.id, false));
    this.batchUpdateSelections(desiredSelections);
  };

  getDisplayText = (): string => {
    const selectedChildCount = this.getChildOptions().filter((c) => c.selected).length;
    const parts: string[] = [];
    this.getParentOptions().forEach((option) => {
      if (this.isReferralParent(option)) {
        if (selectedChildCount > 0) parts.push(`Referral (${selectedChildCount})`);
      } else if (option.selected) {
        parts.push(option.label || option.name || option.value);
      }
    });
    return parts.join(', ');
  };

  renderCheckboxItem = (option: ContentItem, isChild: boolean, parentOption?: ContentItem) => {
    const isParent = this.isParentWithChildren(option);
    const parentState = isParent ? this.getParentState(option) : null;
    const isIndeterminate = parentState === 'indeterminate';

    return (
      <label
        key={option.id}
        className={cx('nested-multiselect__option', {
          'nested-multiselect__option--child': isChild,
          'nested-multiselect__option--parent': isParent
        })}
      >
        <input
          type="checkbox"
          checked={isChild ? option.selected : isParent ? parentState === 'all' || parentState === 'indeterminate' : option.selected}
          ref={(el) => {
            if (el) el.indeterminate = isIndeterminate;
          }}
          onChange={() => {
            if (isChild && parentOption) {
              this.handleChildClick(option, parentOption);
            } else {
              this.handleParentClick(option);
            }
          }}
          className={cx('nested-multiselect__checkbox', { 'nested-multiselect__checkbox--indeterminate': isIndeterminate })}
        />
        <span className="nested-multiselect__label">{option.label}</span>
      </label>
    );
  };

  render() {
    const { chartFormField, isDisabled, ariaRequired } = this.props;
    const { isOpen } = this.state;
    const { name, disabled } = chartFormField;
    const shouldDisable = isDisabled || disabled;
    const displayText = this.getDisplayText();
    const parentOptions = this.getParentOptions();

    return (
      <div className={cx('nested-multiselect', name)} ref={(ref) => (this.wrapperRef = ref)}>
        <button
          type="button"
          className={cx('nested-multiselect__trigger', {
            'nested-multiselect__trigger--open': isOpen,
            'nested-multiselect__trigger--disabled': shouldDisable
          })}
          onClick={() => !shouldDisable && this.setState({ isOpen: !isOpen })}
          disabled={shouldDisable}
          aria-required={ariaRequired}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={chartFormField.label}
          aria-controls={`${name}-listbox`}
        >
          <span className={cx('nested-multiselect__placeholder', { 'nested-multiselect__placeholder--has-value': !!displayText })}>
            {displayText || 'Select'}
          </span>
          <span className="nested-multiselect__arrow" />
        </button>

        {isOpen && (
          <div className="nested-multiselect__dropdown">
            <div className="nested-multiselect__actions">
              <button type="button" className="nested-multiselect__action-btn nested-multiselect__action-btn--reset" onClick={this.clearAllSelections}>
                Reset
              </button>
              <button type="button" className="nested-multiselect__action-btn nested-multiselect__action-btn--done" onClick={() => this.setState({ isOpen: false })}>
                Done
              </button>
            </div>
            <div id={`${name}-listbox`} role="listbox" className="nested-multiselect__options">
              {parentOptions.map((option) => {
                const children = this.isReferralParent(option) ? this.getChildOptions() : [];
                return (
                  <div key={option.id}>
                    {this.renderCheckboxItem(option, false)}
                    {children.map((child) => this.renderCheckboxItem(child, true, option))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    );
  }
}

export default NestedMultiselectDropdown;
